/**
 * Sticky Colour Selector — source of truth for Discover the Pen colour.
 *
 * Event:  kb:pen-colour-change
 * Mirror: document.documentElement[data-kb-pen-colour]
 * Read:   element.getSelectedColour() | window.KBPenColour.get()
 *
 * See docs/shared-pen-colour.md
 */
(() => {
  const EVENT_NAME = 'kb:pen-colour-change';
  const LEGACY_EVENT_NAME = 'colors-selection-banner:change';
  const BUILDER_OPEN_EVENT = 'kb:builder:open';
  const ROOT_ATTR = 'data-kb-pen-colour';

  const publishColourState = (detail) => {
    document.documentElement.setAttribute(ROOT_ATTR, detail.colorHandle || '');

    window.KBPenColour = {
      get() {
        return { ...detail };
      },
      eventName: EVENT_NAME,
      rootAttribute: ROOT_ATTR,
    };
  };

  const dispatchColourChange = (target, detail) => {
    publishColourState(detail);

    const eventInit = {
      bubbles: true,
      composed: true,
      detail,
    };

    target.dispatchEvent(new CustomEvent(EVENT_NAME, eventInit));
    target.dispatchEvent(new CustomEvent(LEGACY_EVENT_NAME, eventInit));
    document.dispatchEvent(new CustomEvent(EVENT_NAME, eventInit));
  };

  if (!customElements.get('sticky-colour-selector')) {
    class StickyColourSelector extends HTMLElement {
      constructor() {
        super();

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.selectedColour = null;
      }

      connectedCallback() {
        this.swatches = Array.from(
          this.querySelectorAll('[data-sticky-colour-selector-swatch]')
        );
        this.links = Array.from(
          this.querySelectorAll('[data-sticky-colour-selector-link]')
        );

        if (!this.swatches.length) {
          return;
        }

        const initialSwatch =
          this.swatches.find((swatch) =>
            swatch.classList.contains('sticky-colour-selector__swatch--active')
          ) || this.swatches[0];

        this.activateColor(initialSwatch.dataset.blockId, { isInitial: true });

        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);

        if (window.Shopify?.designMode) {
          document.addEventListener(
            'shopify:block:select',
            this.handleBlockSelect
          );
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);

        if (window.Shopify?.designMode) {
          document.removeEventListener(
            'shopify:block:select',
            this.handleBlockSelect
          );
        }
      }

      getSelectedColour() {
        return this.selectedColour ? { ...this.selectedColour } : null;
      }

      handleClick(event) {
        const customise = event.target.closest(
          '[data-sticky-colour-selector-customise]'
        );

        if (customise && this.contains(customise)) {
          event.preventDefault();
          this.openConfigurator();
          return;
        }

        const swatch = event.target.closest(
          '[data-sticky-colour-selector-swatch]'
        );

        if (!swatch || !this.contains(swatch)) {
          return;
        }

        this.activateColor(swatch.dataset.blockId);
      }

      openConfigurator() {
        document.dispatchEvent(
          new CustomEvent(BUILDER_OPEN_EVENT, {
            bubbles: true,
            composed: true,
            detail: { source: 'sticky-colour-selector' },
          })
        );
      }

      handleKeydown(event) {
        const currentSwatch = event.target.closest(
          '[data-sticky-colour-selector-swatch]'
        );

        if (!currentSwatch || !this.contains(currentSwatch)) {
          return;
        }

        const currentIndex = this.swatches.indexOf(currentSwatch);
        let nextIndex;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = (currentIndex + 1) % this.swatches.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex =
              (currentIndex - 1 + this.swatches.length) % this.swatches.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.swatches.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();

        const nextSwatch = this.swatches[nextIndex];
        nextSwatch.focus();
        this.activateColor(nextSwatch.dataset.blockId);
      }

      handleBlockSelect(event) {
        const swatch = event.target.closest(
          '[data-sticky-colour-selector-swatch]'
        );

        if (!swatch || !this.contains(swatch)) {
          return;
        }

        this.activateColor(swatch.dataset.blockId);
      }

      activateColor(blockId, { isInitial = false } = {}) {
        if (!blockId) {
          return;
        }

        const activeSwatch = this.swatches.find(
          (swatch) => swatch.dataset.blockId === blockId
        );

        if (!activeSwatch) {
          return;
        }

        const colorName = activeSwatch.dataset.colorName || '';
        const colorHandle = activeSwatch.dataset.colorHandle || '';
        const sectionId = this.dataset.sectionId || '';

        this.swatches.forEach((swatch) => {
          const isActive = swatch.dataset.blockId === blockId;

          swatch.classList.toggle(
            'sticky-colour-selector__swatch--active',
            isActive
          );
          swatch.setAttribute('aria-selected', isActive ? 'true' : 'false');
          swatch.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.links.forEach((link) => {
          const isActive = link.dataset.blockId === blockId;

          link.classList.toggle(
            'sticky-colour-selector__link--active',
            isActive
          );
          link.hidden = !isActive;
        });

        this.dataset.selectedColor = colorName;
        this.dataset.selectedColorHandle = colorHandle;
        this.dataset.selectedBlockId = blockId;

        const detail = {
          blockId,
          colorName,
          colorHandle,
          sectionId,
          source: 'sticky-colour-selector',
          isInitial,
        };

        this.selectedColour = detail;

        const emit = () => dispatchColourChange(this, detail);

        if (isInitial) {
          window.requestAnimationFrame(emit);
          return;
        }

        emit();
      }
    }

    customElements.define('sticky-colour-selector', StickyColourSelector);
  }
})();
